import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { FiClock, FiSend, FiCheckCircle, FiXCircle, FiRefreshCw, FiSearch, FiActivity, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

/* ── Animated Number Counter ── */
const AnimatedNumber = ({ value, duration = 1200 }) => {
  const [display, setDisplay] = useState(0);
  const numVal = typeof value === 'string' ? parseInt(value.replace(/,/g, ''), 10) : value;
  useEffect(() => {
    if (isNaN(numVal) || numVal === 0) { setDisplay(0); return; }
    let start = 0;
    const startTime = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      start = Math.floor(eased * numVal);
      setDisplay(start);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [numVal, duration]);
  return <>{display.toLocaleString()}</>;
};

export default function UserActivity() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);

  const fetchActivity = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page });
      const { data: res } = await api.get(`/user/activity?${params}`);
      setData(res);
    } catch {
      toast.error('Failed to load activity');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  if (loading) return (
    <div className="loading-spinner">
      <div className="spinner" />
      <div className="loading-text">Loading...</div>
      <div className="loading-dots"><span /><span /><span /></div>
    </div>
  );

  const summary = data?.summary || {};
  const campaignActivity = data?.campaignActivity || [];
  const auditLogs = data?.auditLogs || [];
  const totalPages = data?.pages || 1;

  // Build unified timeline from campaign activity + audit logs
  const activities = [];

  campaignActivity.forEach(c => {
    activities.push({
      type: 'campaign', campaign: c.campaignName, time: c.createdAt,
      icon: <FiSend />, color: '#7ea52e',
      desc: `Campaign "${c.campaignName}" — ${c.sent} sent, ${c.failed} failed (${c.type?.replace('_', ' ')})`,
      status: c.status, sent: c.sent, failed: c.failed
    });
    if (c.sent > 0) {
      activities.push({
        type: 'sent', campaign: c.campaignName, time: c.createdAt,
        icon: <FiCheckCircle />, color: '#7ea52e',
        desc: `Delivered ${c.sent} messages for "${c.campaignName}"`,
        sent: c.sent
      });
    }
    if (c.failed > 0) {
      activities.push({
        type: 'failed', campaign: c.campaignName, time: c.createdAt,
        icon: <FiXCircle />, color: '#1a1a18',
        desc: `${c.failed} messages failed in "${c.campaignName}"`,
        failed: c.failed
      });
    }
  });

  auditLogs.forEach(log => {
    activities.push({
      type: 'audit', campaign: log.details || log.action, time: log.createdAt,
      icon: <FiActivity />, color: '#1a1a18',
      desc: `${log.action}: ${log.details || ''}`,
    });
  });

  activities.sort((a, b) => new Date(b.time) - new Date(a.time));

  const filtered = filter === 'all' ? activities
    : filter === 'sent' ? activities.filter(a => a.type === 'sent')
    : filter === 'failed' ? activities.filter(a => a.type === 'failed')
    : filter === 'campaign' ? activities.filter(a => a.type === 'campaign')
    : activities;

  const filterButtons = [
    { key: 'all', label: 'All', icon: <FiActivity size={12} /> },
    { key: 'campaign', label: 'Campaigns', icon: <FiSend size={12} /> },
    { key: 'sent', label: 'Delivered', icon: <FiCheckCircle size={12} /> },
    { key: 'failed', label: 'Failed', icon: <FiXCircle size={12} /> },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Activity</h1>
          <p className="page-subtitle">Your campaign activity log &bull; {summary.totalLogs || 0} audit events</p>
        </div>
        <button className="btn btn-secondary" onClick={() => { setPage(1); fetchActivity(); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FiRefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Summary Stats */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        {[
          { icon: <FiSend />, value: summary.totalCampaigns || 0, label: 'Campaigns', cls: 'blue', accent: '#7ea52e' },
          { icon: <FiCheckCircle />, value: summary.totalSent || 0, label: 'Delivered', cls: 'green', accent: '#7ea52e' },
          { icon: <FiXCircle />, value: summary.totalFailed || 0, label: 'Failed', cls: 'red', accent: '#1a1a18' },
          { icon: <FiActivity />, value: summary.totalLogs || 0, label: 'Audit Events', cls: 'purple', accent: '#1a1a18' },
        ].map((s, i) => (
          <div key={i} className="stat-card" style={{ borderBottom: `3px solid ${s.accent}`, animationDelay: `${0.05 + i * 0.08}s` }}
            onMouseMove={e => { const r = e.currentTarget.getBoundingClientRect(); e.currentTarget.style.setProperty('--mouse-x', `${((e.clientX - r.left) / r.width) * 100}%`); e.currentTarget.style.setProperty('--mouse-y', `${((e.clientY - r.top) / r.height) * 100}%`); }}>
            <div className={`stat-card-icon ${s.cls}`}>{s.icon}</div>
            <div className="stat-card-value"><AnimatedNumber value={s.value} duration={1000 + i * 200} /></div>
            <div className="stat-card-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter Chips */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {filterButtons.map(f => (
          <button key={f.key} className={`btn ${filter === f.key ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter(f.key)}
            style={{ fontSize: 12, padding: '6px 16px', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
            {f.icon} {f.label}
          </button>
        ))}
      </div>

      {/* Activity Timeline */}
      {filtered.length > 0 ? (
        <div className="card">
          <div className="timeline">
            {filtered.map((act, i) => (
              <div key={i} className="timeline-item" style={{ animation: `revealUp 0.4s cubic-bezier(0.22,1,0.36,1) ${0.05 + i * 0.05}s both` }}>
                <div className="timeline-dot" style={{ background: act.color, boxShadow: `0 0 0 4px ${act.color}20` }} />
                <div className="timeline-content">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ color: act.color, fontSize: 16 }}>{act.icon}</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{act.campaign}</span>
                    <span className={`badge badge-${act.type === 'failed' ? 'danger' : act.type === 'sent' ? 'success' : 'info'}`} style={{ fontSize: 10, padding: '2px 8px' }}>
                      {act.type}
                    </span>
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>{act.desc}</p>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FiClock size={11} /> {new Date(act.time).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon"><FiSearch size={40} /></div>
            <h3>No activity found</h3>
            <p>{filter !== 'all' ? 'Try changing the filter' : 'Your campaign activity will appear here'}</p>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 20 }}>
          <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
            style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
            <FiChevronLeft size={14} /> Prev
          </button>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Page {page} of {totalPages}</span>
          <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
            style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
            Next <FiChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
